import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { OwnerNav } from "@/components/admin/OwnerNav";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarClock, Loader2, Plus, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = [
  { i: 1, key: "mon", label: "Mon" }, { i: 2, key: "tue", label: "Tue" },
  { i: 3, key: "wed", label: "Wed" }, { i: 4, key: "thu", label: "Thu" },
  { i: 5, key: "fri", label: "Fri" }, { i: 6, key: "sat", label: "Sat" },
  { i: 7, key: "sun", label: "Sun" },
];

// Owner-scoped availability admin (per card). Reuses the existing canonical
// availability collection via GET/PUT /admin/cards/{card_id}/availability.
// No parallel model. Backend AvailabilityIn schema is honoured 1:1.
export default function AvailabilityAdmin() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState("");
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newBlock, setNewBlock] = useState({ start: "", end: "", reason: "" });

  useEffect(() => {
    api.get("/admin/cards").then(({ data }) => {
      const list = (data || []);
      setCards(list);
      if (list[0]) setCardId(list[0].id);
    }).catch(() => setCards([]));
  }, []);

  useEffect(() => {
    if (!cardId) return;
    setAvail(null);
    api.get(`/admin/cards/${cardId}/availability`).then(({ data }) => setAvail(data)).catch(() => setAvail({ days: [1,2,3,4,5], start: "09:00", end: "18:00", buffer_before: 0, buffer_after: 15, min_notice_hours: 2, max_days: 60, slot_interval: 30, blocked: [] }));
  }, [cardId]);

  const toggleDay = (i) => setAvail((a) => ({ ...a, days: a.days.includes(i) ? a.days.filter((x) => x !== i) : [...a.days, i].sort((x, y) => x - y) }));

  const addBlock = () => {
    if (!newBlock.start || !newBlock.end) { toast.error("Start and end dates required"); return; }
    setAvail((a) => ({ ...a, blocked: [...(a.blocked || []), { ...newBlock }] }));
    setNewBlock({ start: "", end: "", reason: "" });
  };
  const removeBlock = (idx) => setAvail((a) => ({ ...a, blocked: a.blocked.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!cardId || !avail) return;
    setBusy(true);
    try {
      const payload = {
        days: avail.days || [],
        start: avail.start || "09:00",
        end: avail.end || "18:00",
        buffer_before: Number(avail.buffer_before) || 0,
        buffer_after: Number(avail.buffer_after) || 0,
        min_notice_hours: Number(avail.min_notice_hours) || 0,
        max_days: Number(avail.max_days) || 60,
        slot_interval: Number(avail.slot_interval) || 30,
        blocked: (avail.blocked || []).map((b) => ({ start: b.start, end: b.end, reason: b.reason || "" })),
      };
      await api.put(`/admin/cards/${cardId}/availability`, payload);
      toast.success("Availability saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const inputCls = "rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none focus:border-[#D6A653]/70";
  const cardOptions = useMemo(() => cards.map((c) => ({ id: c.id, label: `/${c.slug}` })), [cards]);

  return (
    <div className="aria-dark relative min-h-screen bg-[#050607] text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="availability-page">
      <div className="grain-overlay" style={{ opacity: 0.04 }} />
      <OwnerNav active="meetings" />
      <main className="relative mx-auto max-w-4xl px-4 py-8 sm:px-8">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate("/meetings")} className="text-white/50 hover:text-white" data-testid="avail-back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex-1">
            <h2 className="flex items-center gap-2 text-2xl font-light tracking-tight text-white">
              <CalendarClock className="h-5 w-5 text-[#D6A653]" /> Availability
            </h2>
            <p className="text-sm text-white/45">Working days, hours, buffers, and blocked dates for public booking.</p>
          </div>
          {cardOptions.length > 1 && (
            <Select value={cardId} onValueChange={setCardId}>
              <SelectTrigger className="h-9 w-40 border-white/12 bg-white/[0.03] text-xs text-white" data-testid="avail-card"><SelectValue placeholder="Card" /></SelectTrigger>
              <SelectContent className="aria-pop border-white/10 bg-[#0A0B0D] text-white">
                {cardOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {!avail ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#D6A653]" /></div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-2xl border border-white/10 bg-[#0A0B0D] p-5" data-testid="avail-weekly">
              <p className="mb-3 text-xs uppercase tracking-wider text-[#D6A653]">Working days & hours</p>
              <div className="mb-4 flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => {
                  const on = (avail.days || []).includes(d.i);
                  return (
                    <button key={d.i} onClick={() => toggleDay(d.i)} data-testid={`avail-day-${d.key}`}
                      className={`rounded-full border px-3.5 py-1.5 text-sm ${on ? "border-[#D6A653] bg-[#D6A653]/12 text-white" : "border-white/10 text-white/55 hover:border-white/25"}`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <label className="flex flex-col gap-1 text-xs text-white/50">Start
                  <input type="time" value={avail.start} onChange={(e) => setAvail({ ...avail, start: e.target.value })} className={inputCls} data-testid="avail-start" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">End
                  <input type="time" value={avail.end} onChange={(e) => setAvail({ ...avail, end: e.target.value })} className={inputCls} data-testid="avail-end" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Buffer before (min)
                  <input type="number" min="0" max="240" step="5" value={avail.buffer_before} onChange={(e) => setAvail({ ...avail, buffer_before: e.target.value })} className={inputCls} data-testid="avail-buffer-before" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Buffer after (min)
                  <input type="number" min="0" max="240" step="5" value={avail.buffer_after} onChange={(e) => setAvail({ ...avail, buffer_after: e.target.value })} className={inputCls} data-testid="avail-buffer-after" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Min notice (hours)
                  <input type="number" min="0" max="168" value={avail.min_notice_hours} onChange={(e) => setAvail({ ...avail, min_notice_hours: e.target.value })} className={inputCls} data-testid="avail-min-notice" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Booking window (days)
                  <input type="number" min="1" max="365" value={avail.max_days} onChange={(e) => setAvail({ ...avail, max_days: e.target.value })} className={inputCls} data-testid="avail-max-days" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Slot interval (min)
                  <input type="number" min="5" max="240" step="5" value={avail.slot_interval} onChange={(e) => setAvail({ ...avail, slot_interval: e.target.value })} className={inputCls} data-testid="avail-slot-interval" />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-white/10 bg-[#0A0B0D] p-5" data-testid="avail-blocked">
              <p className="mb-3 text-xs uppercase tracking-wider text-[#D6A653]">Blocked dates</p>
              <div className="mb-3 grid grid-cols-1 items-end gap-2 sm:grid-cols-4">
                <label className="flex flex-col gap-1 text-xs text-white/50">Start
                  <input type="date" value={newBlock.start} onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })} className={inputCls} data-testid="avail-block-start" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">End
                  <input type="date" value={newBlock.end} onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })} className={inputCls} data-testid="avail-block-end" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-white/50">Reason (optional)
                  <input type="text" value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} className={inputCls} placeholder="Vacation, offsite…" data-testid="avail-block-reason" />
                </label>
                <button onClick={addBlock} data-testid="avail-block-add" className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#D6A653] px-3.5 py-2 text-sm font-medium text-black hover:brightness-110">
                  <Plus className="h-4 w-4" /> Add block
                </button>
              </div>
              {(avail.blocked || []).length === 0 ? (
                <p className="text-xs text-white/40">No blocked dates.</p>
              ) : (
                <ul className="space-y-1.5">
                  {(avail.blocked || []).map((b, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/85" data-testid={`avail-block-row-${i}`}>
                      <span>{b.start} → {b.end}{b.reason ? ` · ${b.reason}` : ""}</span>
                      <button onClick={() => removeBlock(i)} className="rounded-md border border-red-500/25 p-1.5 text-red-300/80 hover:bg-red-500/10" data-testid={`avail-block-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="flex justify-end">
              <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-5 py-2.5 text-sm font-medium text-black hover:brightness-110 disabled:opacity-50" data-testid="avail-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save availability
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
